import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, MessageCircle } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

export default function Messages() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(params.get("to") || null);
  const [other, setOther] = useState(null);
  const [thread, setThread] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadConversations = useCallback(() => {
    api.get("/conversations").then(({ data }) => setConversations(data.conversations)).catch(() => {});
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // resolve "other" user info
  useEffect(() => {
    if (!activeId) { setOther(null); return; }
    const known = conversations.find((c) => c.user_id === activeId);
    if (known) { setOther({ name: known.name, avatar: known.avatar, role: known.role }); return; }
    api.get(`/users/${activeId}/public`).then(({ data }) => setOther(data.user)).catch(() => {});
  }, [activeId, conversations]);

  // poll thread
  useEffect(() => {
    if (!activeId) { setThread([]); return; }
    let alive = true;
    const fetchThread = () =>
      api.get(`/messages/${activeId}`).then(({ data }) => { if (alive) setThread(data.messages); }).catch(() => {});
    fetchThread();
    const id = setInterval(fetchThread, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [activeId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const openConv = (uid) => { setActiveId(uid); setParams({ to: uid }); };
  const closeConv = () => { setActiveId(null); setParams({}); loadConversations(); };

  const send = async () => {
    const content = text.trim();
    if (!content || !activeId) return;
    setText("");
    setThread((prev) => [...prev, { id: `tmp-${Date.now()}`, from_user_id: user.id, content, created_at: new Date().toISOString() }]);
    try {
      await api.post("/messages", { to_user_id: activeId, content });
    } catch { /* ignore */ }
  };

  // ---------- conversation list ----------
  if (!activeId) {
    return (
      <div className="animate-fade-in space-y-4">
        <h1 className="font-heading text-2xl font-extrabold">{t("messages")}</h1>
        {conversations.length === 0 && (
          <div className="py-16 text-center text-sm text-slate-500" data-testid="no-conversations">
            <MessageCircle className="mx-auto mb-2 text-slate-600" size={28} />
            {t("noConversations")}
          </div>
        )}
        <div className="space-y-2" data-testid="conversation-list">
          {conversations.map((c) => (
            <button
              key={c.user_id}
              onClick={() => openConv(c.user_id)}
              data-testid={`conversation-${c.user_id}`}
              className="flex w-full items-center gap-3 rounded-2xl border border-cardline bg-card2/60 p-3 text-left transition-colors hover:border-gold/60"
            >
              <img src={c.avatar} alt={c.name} className="h-12 w-12 rounded-full object-cover" />
              <div className="min-w-0 flex-1">
                <div className="font-heading text-sm font-semibold">{c.name}</div>
                <div className="truncate text-xs text-slate-400">{c.last}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- chat thread ----------
  return (
    <div className="animate-fade-in flex h-[calc(100vh-11rem)] flex-col">
      <div className="flex items-center gap-3 border-b border-cardline pb-3">
        <button onClick={closeConv} data-testid="chat-back-btn" className="grid h-9 w-9 place-items-center rounded-full border border-cardline bg-ink2/60 text-slate-300">
          <ArrowLeft size={18} />
        </button>
        <img src={other?.avatar} alt={other?.name} className="h-10 w-10 rounded-full object-cover" />
        <div>
          <div className="font-heading text-sm font-semibold">{other?.name || "…"}</div>
          <div className="text-[11px] text-emerald-400">● en ligne</div>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto py-4" data-testid="chat-thread">
        {thread.length === 0 && <p className="py-10 text-center text-sm text-slate-500">{t("noMessages")}</p>}
        {thread.map((m) => {
          const mine = m.from_user_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-gold text-ink" : "border border-cardline bg-card2/70 text-slate-100"}`}>
                {m.content}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-cardline pt-3">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("typeMessage")}
          className="flex-1 rounded-full border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold"
        />
        <button type="submit" data-testid="chat-send-btn" disabled={!text.trim()} className="grid h-11 w-11 place-items-center rounded-full bg-gold text-ink disabled:opacity-50">
          <Send size={18} />
        </button>
      </form>
    </div>
  );
}
